
import { useState } from "react";

import { plural } from "@/shared/text/plural";
import type { PoolPerformanceResponse } from "@/shared/api";
import { isNegative, signedMoney } from "@/shared/money/money";
import { ABSENT, percent } from "@/shared/metric/metric";
import { humanAge } from "@/shared/time/age";
import { PANEL_HEADER, PANEL_TITLE } from "@/design-system/Panel";


function duration(value: number | null): string {
  return value === null ? ABSENT : humanAge(value);
}

export function PoolPerformance({
  pools,
  selected = null,
  onSelect,
}: {
  readonly pools: readonly PoolPerformanceResponse[];
  readonly selected?: string | null;
  readonly onSelect?: (pool: string | null) => void;
}): React.JSX.Element {
  // Controlled when a chart is listening, self-managed when there is none.
  // A screen without the chart still lets a reader mark the pool they are
  // reading — the selection is a reading aid before it is a chart control.
  const [own, setOwn] = useState<string | null>(null);
  const current = onSelect === undefined ? own : selected;
  const choose = onSelect ?? setOwn;

  return (
    <section
      data-testid="pool-performance"
      className="min-w-0 overflow-hidden rounded-panel border border-border-subtle bg-surface-default xl:col-span-4"
    >
      <div className={`${PANEL_HEADER} justify-between gap-3`}>
        <h2 className={PANEL_TITLE}>Результат по пулам</h2>
        {/* Counted in words. A bare "2" beside a heading is a number the
            reader has to guess the unit of. */}
        <span className="text-helper-sm text-text-muted" data-testid="pool-count">
          {`${String(pools.length)} ${plural(pools.length, "пул", "пула", "пулов")}`}
        </span>
      </div>
      {pools.length === 0 ? (
        <p className="p-4 text-body-main text-text-muted">
          За период нет закрытых циклов по пулам.
        </p>
      ) : (
        <ul className="grid min-w-0 gap-px bg-border-subtle">
          {pools.map((pool) => (
            <PoolModule
              key={pool.pool}
              pool={pool}
              selected={pool.pool === current}
              onSelect={() => {
                choose(pool.pool === current ? null : pool.pool);
              }}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One pool, compact enough that two sit side by side and are compared.
 *
 * The accordion this replaces was a full-width row per pool that opened into
 * nine more figures. Two pools filled a screen and compared nothing: a reader
 * had to open both and remember the first.
 */
function PoolModule({
  pool,
  selected,
  onSelect,
}: {
  readonly pool: PoolPerformanceResponse;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): React.JSX.Element {
  const figures: readonly (readonly [string, string])[] = [
    ["Циклы", String(pool.cycles)],
    ["Доля прибыльных", pool.winrate === null ? ABSENT : percent(pool.winrate)],
    ["Ср. цикл", duration(pool.average_seconds)],
    ["Медиана", duration(pool.median_seconds)],
    // Net over capital-days: what one USDT of capital earned per day held.
    [
      "Доход в день",
      pool.capital_turnover === null ? ABSENT : percent(pool.capital_turnover, 2),
    ],
  ];

  return (
    <li className="min-w-0" data-testid={`pool-${pool.pool}`}>
      <button
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        className={`flex w-full min-w-0 cursor-pointer flex-col gap-2 p-3 text-left transition-colors duration-150 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring ${
          selected ? "bg-surface-selected" : "bg-surface-default hover:bg-surface-hover"
        }`}
      >
        <span className="flex min-w-0 items-baseline justify-between gap-3">
          <span className="flex min-w-0 flex-col">
            <span className="font-mono text-body-main text-text-primary">{pool.pool}</span>
          </span>
          <span
            className={`flex shrink-0 items-baseline gap-1 text-metric-md tabular-nums ${
              isNegative(pool.realised_net) ? "text-status-negative" : "text-status-positive"
            }`}
          >
            {signedMoney(pool.realised_net)}
            <span className="text-helper-sm font-normal text-text-muted">USDT</span>
          </span>
        </span>

        <span className="grid grid-cols-3 gap-x-3 gap-y-1.5">
          {figures.map(([label, value]) => (
            <span key={label} className="flex min-w-0 flex-col">
              <span className="truncate text-label-caps text-text-muted">{label}</span>
              <span className="text-helper-sm tabular-nums text-text-secondary">{value}</span>
            </span>
          ))}
        </span>
      </button>
    </li>
  );
}
